/**
 * Icon conventions — one stroke weight, one size ladder.
 *
 * The app already standardised on `lucide-react` (TRD §5.2, DESIGN-SYSTEM.md),
 * which is the right call. What it did not have was a *convention*: icons were
 * being placed at `size-4`, `size-5`, `size-[17px]`, `h-6 w-6` and Lucide's
 * default stroke of 2 in whatever combination each screen reached for, which is
 * what makes an otherwise-consistent icon set read as assembled.
 *
 * Two constants fix that:
 *
 *   ICON_STROKE — 1.75, not Lucide's 2. At the 16-18px sizes this product
 *   actually uses, a 2px stroke on a 24px viewport grid rendered visually
 *   heavier than the stems of Manrope, the body face it was tuned against;
 *   1.75 sat on the same optical weight as the type. Onest, the body face
 *   since 2026-10-09, has a slightly heavier regular stem (0.085em against
 *   Manrope's 0.073em, measured on «l»), which puts it between the two
 *   strokes; the stroke was not re-tuned with the font change. Anything 24px
 *   and up may drop to 1.5 via `ICON_STROKE_LG`.
 *
 *   ICON — the size ladder, in the same 2px rhythm as the spacing scale. Sizes
 *   are px numbers because Lucide takes `size` as a number and a Tailwind class
 *   cannot be read back by the component for its viewBox.
 *
 * Tap targets are a separate concern: an icon at `ICON.md` still needs a ≥44px
 * hit area from its button, never from the glyph.
 */

/** Default stroke for icons at 14-20px. */
export const ICON_STROKE = 1.75;

/** Stroke for display-scale icons (24px+), where 1.75 reads heavy. */
export const ICON_STROKE_LG = 1.5;

/** The size ladder. Use the token, not a literal. */
export const ICON = {
  /** Inline with small caps / meta text. */
  xs: 14,
  /** Inline with body text, chips, list meta. */
  sm: 16,
  /** The default: buttons, nav, controls. */
  md: 18,
  /** Section headers, emphasised affordances. */
  lg: 20,
  /** Empty states, feature marks. */
  xl: 24,
  /** Hero / illustration scale. */
  display: 32,
} as const;

export type IconSize = keyof typeof ICON;

/**
 * Props every Lucide icon in the app should spread, so a call site cannot
 * silently reintroduce the default stroke:
 *
 *   <Store {...iconProps("md")} />
 */
export function iconProps(size: IconSize = "md"): {
  size: number;
  strokeWidth: number;
  "aria-hidden": true;
} {
  const px = ICON[size];
  return {
    size: px,
    strokeWidth: px >= ICON.xl ? ICON_STROKE_LG : ICON_STROKE,
    "aria-hidden": true,
  };
}
